import { useState, useEffect, useCallback } from "react";
import { Button, Chip, CircularProgress, Typography } from "@material-ui/core";
import { api } from "../../services/apiService";

const DAILY_CAP = 25; // mirrors qlearn.mentorship.tags.daily-cap (server-enforced)
const DAY_MS = 24 * 60 * 60 * 1000;

const STATUS_CHIP = {
  OPEN: { label: "Open", cls: "tag-chip--open" },
  ACKNOWLEDGED: { label: "Seen by tutor", cls: "tag-chip--ack" },
  RESOLVED: { label: "Resolved", cls: "tag-chip--resolved" },
};

/**
 * Student-side "Questions I tagged" (design §10.4, slice T3) — rendered on the
 * /mentorships page for students when the tag flags are on. Lists the caller's
 * own tags (always visible to their author, §10.3-R1), lets them retract, and
 * shows the remaining daily create-budget per tutor (client-side estimate of
 * the server's 25-creates/24h rate — the server stays authoritative and the
 * count deliberately includes retracted/resolved tags, mirroring the no-refill
 * invariant).
 *
 * XSS discipline (§10.3 / T3 order): the student note is stored raw server-side
 * and defense is at render — notes pass ONLY through JSX text nodes (React
 * output-encodes); no dangerouslySetInnerHTML anywhere in the tag UI.
 */
export default function QuestionTagsSection() {
  const [tags, setTags] = useState(null);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const res = await api.listQuestionTags();
      const status = res?.errStatus || res?.status;
      if (status === 404) {
        // Backend flags off — dark, not an error.
        setTags([]);
        return;
      }
      if (Array.isArray(res)) {
        setTags(res);
      } else {
        setError("Couldn't load your tagged questions. Please try again.");
        setTags(null);
      }
    } catch (e) {
      setError("Couldn't load your tagged questions. Please try again.");
      setTags(null);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const retract = async (id) => {
    setBusyId(id);
    setError("");
    let failed = false;
    try {
      // doDeleteCall resolves {errStatus, errMsg} on failure — inspect, don't catch.
      const res = await api.retractQuestionTag(id);
      failed = !!(res?.errStatus || res?.errMsg);
    } catch (e) {
      failed = true;
    }
    await load();
    setBusyId("");
    // Uniform message: the server's 404 is deliberately identical for
    // missing/not-yours — the UI mirrors that and adds no detail.
    if (failed) setError("Couldn't remove that tag. Please try again.");
  };

  const items = tags || [];
  // Remaining daily creates per tutor: count ALL of my tags created in the
  // last 24h for that tutor — including RESOLVED and (already gone from this
  // list) retracted ones can't be counted client-side, so this is an upper
  // bound; the server rate is authoritative.
  const remainingFor = (tutorUserId) => {
    const used = items.filter(
      (t) =>
        t.tutorUserId === tutorUserId &&
        t.createdAt &&
        Date.now() - new Date(t.createdAt).getTime() < DAY_MS
    ).length;
    return Math.max(0, DAILY_CAP - used);
  };

  // One row per distinct tutor for the budget line.
  const tutorsWithTags = [...new Set(items.map((t) => t.tutorUserId).filter(Boolean))];

  if (tags !== null && items.length === 0 && !error) {
    return null; // nothing tagged yet — stay out of the page's way
  }

  return (
    <div className="mtr-section" data-testid="tag-section">
      <Typography variant="subtitle2" className="mtr-section-title">
        Questions I tagged
      </Typography>
      {error && (
        <Typography className="mtr-error" variant="body2" data-testid="tag-section-error">
          {error}
        </Typography>
      )}
      {tags === null && !error && (
        <div className="mtr-loading">
          <CircularProgress size={20} />
        </div>
      )}
      {items.map((t) => {
        const chip = STATUS_CHIP[t.status] || { label: t.status, cls: "" };
        const live = t.status === "OPEN" || t.status === "ACKNOWLEDGED";
        return (
          <div className="mtr-card mtr-item" key={t.id} data-testid="tag-row">
            <div className="mtr-item-main">
              <div className="tag-row-head">
                <Typography className="mtr-name">
                  {t.counterpartDisplayName || "Your tutor"}
                </Typography>
                <Chip size="small" className={`tag-chip ${chip.cls}`} label={chip.label} />
              </div>
              {/* Note renders as a JSX TEXT NODE only — React output-encodes it,
                  so a <script>/onerror payload in a stored note is inert text. */}
              {t.note && (
                <Typography variant="body2" className="mtr-msg" data-testid="tag-note-text">
                  “{t.note}”
                </Typography>
              )}
              <Typography variant="caption" className="mtr-sub">
                {t.createdAt ? new Date(t.createdAt).toLocaleDateString() : ""}
              </Typography>
            </div>
            {live && (
              <Button
                className="mtr-btn"
                variant="outlined"
                size="small"
                disabled={busyId === t.id}
                onClick={() => retract(t.id)}
                data-testid="tag-retract"
              >
                Remove tag
              </Button>
            )}
          </div>
        );
      })}
      {tutorsWithTags.length > 0 && (
        <Typography variant="caption" className="mtr-sub" data-testid="tag-budget">
          {tutorsWithTags
            .map((id) => {
              const name =
                items.find((t) => t.tutorUserId === id)?.counterpartDisplayName || "your tutor";
              return `${remainingFor(id)} of ${DAILY_CAP} tags left today for ${name}`;
            })
            .join(" · ")}
        </Typography>
      )}
    </div>
  );
}
